// Chat that survives the tab (busy-main-bff#21, AI Brain spec §1/§4/§14).
//
// Until now a conversation lived in Vuex and died with the page: reloading Home lost the thread,
// and opening an Ongoing you had researched yesterday gave you an empty box. The server now keeps
// one session per SCOPE ("home", "ongoing:<id>", "pack:<id>", …) and this module is the client half:
// stored messages in, the turn shapes the two chat surfaces already render out.
//
// Pure on purpose — no store, no axios, no Vue. Everything here is "given these rows, what should be
// on screen", which is exactly the part worth testing without mounting anything.

import { objectRef, refString } from '@/lib/objectRef'
import { normalizeBlocks } from '@/lib/answerBlocks'

/** The global conversation's scope. Every other surface names itself (see lib/askScope.js). */
export const HOME_SCOPE = 'home'

/** How much of a conversation is pulled back on open — a scrollback, not an archive. */
export const RESTORE_MESSAGES = 40

const parseJson = (json) => {
  if (typeof json !== 'string' || !json.trim()) return json
  try { return JSON.parse(json) } catch { return null }
}

/**
 * Stored sources travel as a JSON string; anything unreadable is simply no sources.
 *
 * Since the chat's answer blocks (chat Figma rebuild, WP6) the journal may keep `{ sources, blocks }`
 * in the same column instead of the bare array — the BFF splits it back out, but a row read from
 * anywhere that does not is still understood.
 */
export function parseSources (json) {
  const parsed = parseJson(json)
  if (Array.isArray(parsed)) return parsed
  if (parsed && typeof parsed === 'object' && Array.isArray(parsed.sources)) return parsed.sources
  return []
}

/**
 * The answer's blocks as stored (PLAN §2): `blocksJson` when the BFF sent it, else the `blocks`
 * half of a `{ sources, blocks }` sources column. Null when there are none — the turn is then drawn
 * from its text, exactly as before blocks existed.
 */
export function parseBlocks (blocksJson, sourcesJson) {
  const own = parseJson(blocksJson)
  if (Array.isArray(own) && own.length) return own
  const packed = parseJson(sourcesJson)
  if (packed && !Array.isArray(packed) && Array.isArray(packed.blocks) && packed.blocks.length) return packed.blocks
  return null
}

// A web source has a URL and a mailbox source has a timeline: one list on the wire, two panels on
// screen, and the shape itself is what says which is which. Deriving it beats a flag the server
// would have to remember to set.
const isWebSource = (s) => !!(s && (s.url || s.Url))

// Oldest first, whatever order the page came in (`GET chat/sessions/{id}/messages` pages backwards):
// the pairing below reads the conversation in the order it happened. A page with an undated row is
// left in the order it came — there is nothing to sort it by.
function inOrder (messages) {
  const rows = Array.isArray(messages) ? messages : []
  const times = rows.map((m) => Date.parse(m?.createdUtc || ''))
  if (times.some(Number.isNaN)) return rows
  return rows.map((m, i) => ({ m, t: times[i] })).sort((a, b) => a.t - b.t).map((r) => r.m)
}

/**
 * The stored messages as TURNS: a question with the answer that followed it.
 *
 * The pairing rule is "an assistant message belongs to the last question above it" — the order the
 * conversation happened in, which is the order the store returns. A question with no answer yet
 * (the journal writes both, but a crash between them is possible) still becomes a turn, so nothing
 * the user typed disappears from their own history.
 */
export function turnsFromMessages (messages) {
  const turns = []
  for (const m of inOrder(messages)) {
    const role = String(m?.role || '').toLowerCase()
    const text = m?.text || ''
    if (!text) continue
    if (role === 'user') {
      turns.push({
        id: `chat-${m.id}`,
        messageId: m.id,
        question: text,
        answer: '',
        answerMessageId: null,
        sources: [],
        webSources: [],
        scopeEffective: m.scopeEffective || null,
        fallbackReason: null,
        savedRefs: [],
        blocks: null,
        createdUtc: m.createdUtc || null,
        answeredUtc: null,
      })
    } else if (role === 'assistant') {
      // An answer with nothing above it is a turn of its own rather than a dropped row.
      if (!turns.length || turns[turns.length - 1].answer) {
        turns.push({
          id: `chat-${m.id}`, messageId: null, question: '', answer: '', answerMessageId: null,
          sources: [], webSources: [], scopeEffective: null, fallbackReason: null, savedRefs: [],
          blocks: null, createdUtc: m.createdUtc || null, answeredUtc: null,
        })
      }
      const turn = turns[turns.length - 1]
      const sources = parseSources(m.sourcesJson)
      turn.answer = text
      turn.answerMessageId = m.id
      turn.webSources = sources.filter(isWebSource)
      turn.sources = sources.filter(s => !isWebSource(s))
      turn.scopeEffective = m.scopeEffective || turn.scopeEffective
      turn.fallbackReason = m.fallbackReason || null
      turn.savedRefs = Array.isArray(m.savedRefs) ? m.savedRefs : []
      turn.blocks = parseBlocks(m.blocksJson, m.sourcesJson)
      turn.answeredUtc = m.createdUtc || null
    }
    // A system line is context nobody said out loud — it is not part of the rendered chat.
  }
  return turns
}

/**
 * The answer's scope block, rebuilt for the line the UI already shows (`fallbackLine`). A restored
 * answer must still admit that it searched everything: dropping that on reload would turn a widened
 * answer into one that silently claims to be scoped.
 */
export function scopeOf (turn) {
  if (!turn?.scopeEffective && !turn?.fallbackReason) return null
  return {
    effective: turn.scopeEffective || HOME_SCOPE,
    fallback: !!turn.fallbackReason,
    reason: turn.fallbackReason || null,
  }
}

/**
 * A stored answer whose SUGGESTED task was created afterwards (chat rebuild, frames 09–11): the
 * journal keeps the block as the model wrote it — a `suggestion` — and records the task on the
 * message's saved refs (`task:<id>@busy-task-srv`, store/modules/chatActions.js). Restored, it is
 * drawn as what it became: "Task created · <title>" with View Task, not a second Create button.
 * One saved task per suggestion, in order; anything else is left as it was.
 */
export function createdFromSaved (blocks, refs) {
  let tasks = (Array.isArray(refs) ? refs : [])
    .map((r) => splitRef(String(r || '').trim()))
    .filter(([type, id]) => type === 'task' && id)
    .map(([, id]) => id)
  if (!tasks.length || !Array.isArray(blocks)) return blocks
  // A task the answer itself created is already drawn as created — its ref is not a suggestion's.
  const drawn = new Set(blocks.filter((b) => b?.type === 'entityCreated' && b.id).map((b) => String(b.id)))
  tasks = tasks.filter((id) => !drawn.has(id))
  let next = 0
  return blocks.map((b) => {
    if (b?.type !== 'suggestion' || (b.kind && b.kind !== 'task') || next >= tasks.length) return b
    const id = tasks[next++]
    return {
      ...b,
      type: 'entityCreated',
      kind: 'task',
      id,
      title: String(b.title || ''),
      dueUtc: b.dueUtc || null,
      allDay: !!b.allDay,
      ongoing: b.ongoing || null,
      state: null,
    }
  })
}

/** Restored turns for the Home chat (`timelineSearch.conversation`). */
export function homeConversation (messages) {
  return turnsFromMessages(messages).map(t => ({
    id: t.id,
    messageId: t.answerMessageId || t.messageId,
    question: t.question,
    // Web-ness is not stored; a turn with web links plainly was one.
    web: t.webSources.length > 0,
    answer: t.answer,
    sources: t.sources,
    webSources: t.webSources,
    scope: scopeOf(t),
    savedRefs: t.savedRefs,
    // The answer as the chat draws it (chat Figma rebuild, WP5): the stored blocks when the journal
    // kept any, else the text as one block — the same rule a live answer goes through.
    blocks: createdFromSaved(normalizeBlocks({ blocks: t.blocks, answer: t.answer }), t.savedRefs),
    askedUtc: t.createdUtc,
    answeredUtc: t.answeredUtc,
    followUps: [],
    asking: false,
    restored: true,
  }))
}

/**
 * Restored turns for the research / Ongoing chat (`research.turns`).
 *
 * The saved-item lists a live turn carries (photos, videos, products) are NOT rebuilt: they were
 * never stored, and inventing empty arrays is honest where inventing ids would not be — a restored
 * turn must not hand the workboard tiles whose ids nothing can resolve.
 */
export function researchTurns (messages, titles = {}) {
  return turnsFromMessages(messages).map(t => {
    const saved = savedPack(t.savedRefs)
    return {
      id: t.id,
      messageId: t.answerMessageId || t.messageId,
      question: t.question,
      // Web-ness is not stored (the same gap `homeConversation` reads around): a turn that came back
      // with web links plainly was a web search; one without is the story's chat (busy-my-web#397),
      // and the workspace draws it as a conversation rather than as an empty results page.
      web: t.webSources.length > 0,
      answer: t.answer,
      links: t.webSources.map((s, i) => ({
        id: `${t.id}-web-${i}`, kind: 'link', title: s.title || s.url, url: s.url, snippet: s.snippet || '',
      })),
      photos: [], videos: [], products: [], followUps: [], actions: [], answerChunks: [],
      loading: false,
      streaming: false,
      restored: true,
      ...(saved ? { filedPackId: saved, filedPackTitle: titles[saved] || null } : {}),
    }
  })
}

/** A story's packs as `{ id: title }` — what turns a stored reference back into a readable stamp. */
export function packTitles (story) {
  const out = {}
  for (const pack of story?.packs || []) {
    if (pack?.id && pack?.title) out[String(pack.id)] = pack.title
  }
  return out
}

/** The research pack a turn was filed into, if any — the stamp's subject. */
export function savedPack (refs) {
  for (const ref of Array.isArray(refs) ? refs : []) {
    const match = /^researchpack:([0-9a-f-]{36})/i.exec(String(ref || '').trim())
    if (match) return match[1]
  }
  return null
}

/**
 * "Saved to Prada sunglasses" — the stamp under a turn that produced something.
 *
 * Falls back to the KIND when no title is known ("Saved to research pack"), because a restored turn
 * knows the reference but not the name: the name lives in the story, and a stamp that waited for it
 * would show nothing at all on the surfaces that have not loaded one.
 */
export function savedLabel (refs, titles = {}) {
  const list = (Array.isArray(refs) ? refs : []).map(r => String(r || '').trim()).filter(Boolean)
  if (!list.length) return ''
  const [type, id] = splitRef(list[0])
  const title = titles[id]
  if (title) return `Saved to ${title}`
  const kind = { researchpack: 'research pack', task: 'task', note: 'note', event: 'event', document: 'document' }[type]
  return kind ? `Saved to ${kind}` : 'Saved'
}

function splitRef (ref) {
  const body = String(ref).split('@')[0]
  const colon = body.indexOf(':')
  if (colon <= 0) return [null, null]
  return [body.slice(0, colon).toLowerCase(), body.slice(colon + 1)]
}

/**
 * The canonical references a save produced, from whatever the save flow happens to know:
 * `{ packId, taskId, noteId, eventId, documentId }`. Anything that is not a server id is dropped —
 * a stamp pointing at a client-side draft id could only ever 404.
 */
export function savedRefsFrom ({ packId, taskId, noteId, eventId, documentId } = {}) {
  return [
    ['researchpack', packId],
    ['task', taskId],
    ['note', noteId],
    ['event', eventId],
    ['document', documentId],
  ]
    .map(([type, id]) => refString(objectRef(type, id)))
    .filter(Boolean)
}

/**
 * The stored message id to stamp for a turn the user has just saved from.
 *
 * A turn asked in THIS session has no message id of its own — the answer is journalled server-side,
 * after the response, so the client never sees the row. It is found again by its question, newest
 * first: the same question asked twice should stamp the answer the user is actually looking at.
 */
export function messageIdForQuestion (messages, question) {
  const wanted = String(question || '').trim().toLowerCase()
  if (!wanted) return null
  const turns = turnsFromMessages(messages)
  for (let i = turns.length - 1; i >= 0; i -= 1) {
    if (String(turns[i].question || '').trim().toLowerCase() === wanted) {
      return turns[i].answerMessageId || turns[i].messageId || null
    }
  }
  return null
}

/**
 * What a conversation is CALLED before anyone names it (busy-my-web#450, chat frames).
 *
 * The frames head the page with a SUMMARY of the chat — "Greeting" over the question "Hi" — which
 * is a sentence only the model can write, and nothing on the wire carries one (see the note in
 * store/modules/timelineSearch.js). So the stand-in is the thing the chat actually began with: the
 * first question, trimmed to the header's own width — six words, or 40 characters, whichever comes
 * first, with a single ellipsis when it was cut. A chat with no question yet has no name at all,
 * and the header draws nothing rather than "Untitled".
 */
export function chatTitle (firstQuestion) {
  const q = String(firstQuestion || '').replace(/\s+/g, ' ').trim()
  if (!q) return ''
  const words = q.split(' ')
  let text = words.slice(0, 6).join(' ')
  let cut = words.length > 6
  if (text.length > 40) { text = text.slice(0, 40).trimEnd(); cut = true }
  return cut ? `${text}…` : text
}
